import { useState, useEffect } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { Button, Input, InputNumber, Select, Spin, Empty, message } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import { CoreBadge } from 'src/RnRCommonComponent';
import {
	BADGE_TYPE,
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
} from 'src/RnRCommonComponent/CoreBadge/rewardsConstants.js';
import { fetchS3Bytes } from 'src/certificate-generator/certificatePdfUtils';

const AUTOMATED_TYPE_ID = '449e1150-ddac-485d-8321-537705c5bb40';

const QUERY_MILESTONE_BADGES = gql`
	query QueryBadgesByCompanyIdIndex(
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryBadgesByCompanyIdIndex(
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				companyId
				badgeTypeId
				iconId
				name
				label
				description
				color
				gradientColor
				points
				bonusPoints
				rewardBonus
				rewardMaxBonus
				shape
				category
				milestoneYears
				visibility
				assignmentLimit
				assignmentLevelId
				recipientLevelId
				isPreApproved
				snappyGrantId
				snappyGrant {
					id
					name
					description
					costInCurrency
				}
				certificateConfig {
					id
					name
					key
				}
				badgeRotation
				customImageKey
				customImageSignedUrl
				labelColor
				customIconKey
				customIconSignedUrl
				milestoneRewardId
				milestoneReward {
					id
					name
					image
				}
			}
			nextToken
		}
	}
`;

const UPDATE_BADGE = gql`
	mutation UpdateBadge($input: UpdateBadgeInput!) {
		updateBadge(input: $input) {
			id
			name
			label
			description
			milestoneYears
			certificateConfig {
				id
				name
				key
			}
			milestoneRewardId
			milestoneReward {
				id
				name
				image
			}
		}
	}
`;

const LIST_CERTIFICATE_ASSETS = gql`
	query ListCertificateAssets($companyId: ID!) {
		listCertificateAssets(companyId: $companyId) {
			configs {
				key
				fileName
			}
		}
	}
`;

const MilestoneBadgesCard = ({
	currentUser,
	milestoneRewards,
	refreshRef,
	onBadgesLoaded,
}) => {
	const client = useApolloClient();
	const companyId = currentUser?.company?.id;

	const [badges, setBadges] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingId, setEditingId] = useState(null);
	const [editValues, setEditValues] = useState({});
	const [saving, setSaving] = useState(false);
	const [configOptions, setConfigOptions] = useState([]);
	const [loadingConfigs, setLoadingConfigs] = useState(false);

	const { data: assetsData } = useQuery(LIST_CERTIFICATE_ASSETS, {
		variables: { companyId },
		skip: !companyId,
	});

	const configKeysJson = JSON.stringify(
		(assetsData?.listCertificateAssets?.configs || []).map((c) => c.key)
	);

	useEffect(() => {
		const keys = JSON.parse(configKeysJson);
		if (keys.length === 0) {
			setConfigOptions([]);
			return;
		}

		let cancelled = false;
		setLoadingConfigs(true);
		Promise.all(
			keys.map(async (key) => {
				try {
					const bytes = await fetchS3Bytes(key);
					const json = JSON.parse(new TextDecoder().decode(bytes));
					return {
						label: json.name || key,
						value: json.id,
						configData: { id: json.id, name: json.name, key },
					};
				} catch {
					return {
						label: key,
						value: key,
						configData: { id: key, name: key, key },
					};
				}
			})
		)
			.then((options) => {
				if (!cancelled) {
					setConfigOptions(
						options.sort((a, b) => a.label.localeCompare(b.label))
					);
					setLoadingConfigs(false);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setLoadingConfigs(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [configKeysJson]);

	useEffect(() => {
		if (companyId) {
			fetchBadges();
		}
		if (refreshRef) {
			refreshRef.current = fetchBadges;
		}
	}, [companyId]);

	const fetchBadges = async () => {
		try {
			setLoading(true);
			let allBadges = [];
			let nextToken = null;

			do {
				const { data } = await client.query({
					query: QUERY_MILESTONE_BADGES,
					variables: { companyId, limit: 100, nextToken },
					fetchPolicy: 'network-only',
				});

				if (data?.queryBadgesByCompanyIdIndex?.items) {
					allBadges = [...allBadges, ...data.queryBadgesByCompanyIdIndex.items];
					nextToken = data.queryBadgesByCompanyIdIndex.nextToken;
				} else {
					nextToken = null;
				}
			} while (nextToken);

			const milestoneBadges = allBadges
				.filter((b) => b.badgeTypeId === AUTOMATED_TYPE_ID)
				.map((badge) => ({
					...badge,
					badgeType: BADGE_TYPE.AUTOMATED,
					iconInfo: badge.iconId
						? ICON_COMPLETE_MAP.find((icon) => icon.id === badge.iconId)
						: null,
				}))
				.sort((a, b) => (a.milestoneYears || 0) - (b.milestoneYears || 0));

			setBadges(milestoneBadges);
			if (onBadgesLoaded) onBadgesLoaded(milestoneBadges);
		} catch (error) {
			console.error('Error fetching milestone badges:', error);
			message.error('Failed to load milestone badges');
		} finally {
			setLoading(false);
		}
	};

	const startEditing = (badge) => {
		setEditingId(badge.id);
		setEditValues({
			name: badge.name,
			description: badge.description,
			milestoneYears: badge.milestoneYears,
			certificateConfig: badge.certificateConfig || null,
			milestoneRewardId: badge.milestoneRewardId || null,
		});
	};

	const cancelEditing = () => {
		setEditingId(null);
		setEditValues({});
	};

	const handleSave = async (badge) => {
		try {
			setSaving(true);
			const input = {
				id: badge.id,
				companyId,
				updatedBy: currentUser?.id,
				name: editValues.name,
				description: editValues.description,
				milestoneYears: editValues.milestoneYears,
				milestoneRewardId: editValues.milestoneRewardId || null,
				customImageKey: badge.customImageKey || null,
			};

			if (editValues.certificateConfig) {
				const { __typename, ...certConfig } = editValues.certificateConfig;
				input.certificateConfig = certConfig;
			} else {
				input.certificateConfig = null;
			}

			const { data } = await client.mutate({
				mutation: UPDATE_BADGE,
				variables: { input },
			});

			if (data?.updateBadge) {
				await fetchBadges();
				setEditingId(null);
				setEditValues({});
				message.success('Badge updated!');
			}
		} catch (error) {
			console.error('Error updating badge:', error);
			message.error('Failed to update badge');
		} finally {
			setSaving(false);
		}
	};

	const isEditing = (id) => editingId === id;

	return (
		<div
			style={{
				background: '#fff',
				borderRadius: '14px',
				padding: '24px',
				border: '1px solid #ddd',
				marginBottom: '24px',
			}}
		>
			<div style={{ marginBottom: '20px' }}>
				<h3
					style={{
						fontSize: '18px',
						fontWeight: '600',
						color: '#555',
						margin: 0,
					}}
				>
					Milestone Badges
				</h3>
				<p style={{ color: '#595959', fontSize: '13px', margin: '4px 0 0' }}>
					View and configure badges that are automatically awarded for milestone
					anniversaries.
				</p>
			</div>

			{loading ? (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						padding: '40px 0',
					}}
				>
					<Spin size="large" />
				</div>
			) : badges.length === 0 ? (
				<Empty
					description="No milestone badges found"
					style={{ padding: '40px 0' }}
				/>
			) : (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: '70px 1fr 1fr 100px 1fr 1fr 60px',
							gap: 12,
							padding: '0 12px 8px',
							borderBottom: '1px solid #f0f0f0',
							fontSize: '12px',
							fontWeight: '600',
							color: '#595959',
							textTransform: 'uppercase',
							letterSpacing: '0.5px',
						}}
					>
						<span>Badge</span>
						<span>Name</span>
						<span>Description</span>
						<span>Years</span>
						<span>Certificate</span>
						<span>Reward</span>
						<span />
					</div>

					<AnimatePresence mode="popLayout">
						{badges.map((badge, index) => (
							<motion.div
								key={badge.id}
								layout
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.25, delay: index * 0.03 }}
								style={{
									display: 'grid',
									gridTemplateColumns: '70px 1fr 1fr 100px 1fr 1fr 60px',
									gap: 12,
									alignItems: 'center',
									padding: '10px 12px',
									borderRadius: 10,
									background: '#fafafa',
									border: '1px solid #f0f0f0',
								}}
							>
								<div
									style={{
										width: 58,
										height: 50,
										flexShrink: 0,
										overflow: 'hidden',
										position: 'relative',
									}}
								>
									<div
										style={{
											transform: 'scale(0.35)',
											transformOrigin: 'top left',
											width: 150,
											height: 150,
											position: 'absolute',
											top: 0,
											left: 0,
										}}
									>
										<CoreBadge
											badgeType={BADGE_TYPE.AUTOMATED}
											color={badge.color}
											gradientColor={badge.gradientColor}
											label={badge.label}
											shape={badge.shape}
											badgeRotation={badge.badgeRotation}
											customImageSignedUrl={badge.customImageSignedUrl}
											labelColor={badge.labelColor}
											customIconSignedUrl={badge.customIconSignedUrl}
											iconId={badge.iconId}
											useTilt={false}
										/>
									</div>
								</div>

								{isEditing(badge.id) ? (
									<>
										<Input
											value={editValues.name}
											onChange={(e) =>
												setEditValues((prev) => ({
													...prev,
													name: e.target.value,
												}))
											}
											size="small"
										/>
										<Input.TextArea
											value={editValues.description}
											onChange={(e) =>
												setEditValues((prev) => ({
													...prev,
													description: e.target.value,
												}))
											}
											size="small"
											autoSize={{ minRows: 1, maxRows: 3 }}
										/>
										<InputNumber
											value={editValues.milestoneYears}
											onChange={(val) =>
												setEditValues((prev) => ({
													...prev,
													milestoneYears: val,
												}))
											}
											min={0}
											size="small"
											style={{ width: '100%' }}
										/>
										<Select
											value={editValues.certificateConfig?.id || null}
											onChange={(val) => {
												if (!val) {
													setEditValues((prev) => ({
														...prev,
														certificateConfig: null,
													}));
												} else {
													const option = configOptions.find(
														(o) => o.value === val
													);
													if (option) {
														setEditValues((prev) => ({
															...prev,
															certificateConfig: option.configData,
														}));
													}
												}
											}}
											options={[
												{ label: 'None', value: null },
												...configOptions,
											]}
											size="small"
											style={{ width: '100%' }}
											loading={loadingConfigs}
											allowClear
											placeholder="Select certificate"
										/>
										<Select
											value={editValues.milestoneRewardId || null}
											onChange={(val) =>
												setEditValues((prev) => ({
													...prev,
													milestoneRewardId: val || null,
												}))
											}
											options={[
												{ label: 'None', value: null },
												...milestoneRewards.map((r) => ({
													label: r.name,
													value: r.id,
												})),
											]}
											size="small"
											style={{ width: '100%' }}
											allowClear
											placeholder="Select reward"
										/>
										<div
											style={{
												display: 'flex',
												gap: 4,
												justifyContent: 'center',
											}}
										>
											<Button
												type="text"
												icon={<SaveOutlined style={{ color: '#52c41a' }} />}
												onClick={() => handleSave(badge)}
												size="small"
												loading={saving}
											/>
											<Button
												type="text"
												icon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
												onClick={cancelEditing}
												size="small"
												disabled={saving}
											/>
										</div>
									</>
								) : (
									<>
										<div
											style={{
												fontWeight: 600,
												fontSize: '14px',
												color: '#1a1a2e',
											}}
										>
											{badge.name}
										</div>
										<div
											style={{
												fontSize: '12px',
												color: '#595959',
												overflow: 'hidden',
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
											}}
										>
											{badge.description || '\u00A0'}
										</div>
										<div style={{ fontSize: '14px', color: '#555' }}>
											{badge.milestoneYears != null
												? `${badge.milestoneYears} yr${badge.milestoneYears !== 1 ? 's' : ''}`
												: '--'}
										</div>
										<div style={{ fontSize: '12px', color: '#555' }}>
											{badge.certificateConfig?.name || '--'}
										</div>
										<div style={{ fontSize: '12px', color: '#555' }}>
											{badge.milestoneReward?.name || '--'}
										</div>
										<Button
											type="text"
											icon={<EditOutlined style={{ color: '#1890ff' }} />}
											onClick={() => startEditing(badge)}
											size="small"
										/>
									</>
								)}
							</motion.div>
						))}
					</AnimatePresence>
				</div>
			)}
		</div>
	);
};

export default MilestoneBadgesCard;
